import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {object} VariantData
 * @property {number} id
 * @property {boolean} available
 * @property {string[]} options
 */

/**
 * @typedef {object} Refs
 * @property {HTMLScriptElement} variantsData
 * @property {HTMLElement} [titleRow]
 */

/**
 * Product card size quick-shop: size pills (or a simple "Añadir" button for
 * products without size/variant options). Rendered by Liquid in its final
 * position (below title + price) - no DOM relocation, so no layout shift.
 * @extends {Component<Refs>}
 */
class CardQuickShop extends Component {
  requiredRefs = ['variantsData'];

  /** @type {VariantData[]} */
  #variants = [];

  /** @type {number} */
  #sizeOptionIndex = 0;

  /** @type {'sizes' | 'simple'} */
  #mode = 'sizes';

  /** @type {HTMLElement | null} */
  #productCard = null;

  connectedCallback() {
    super.connectedCallback();

    this.#mode = this.dataset.mode === 'simple' ? 'simple' : 'sizes';
    this.#sizeOptionIndex = Number(this.dataset.sizeOptionIndex || 0);
    this.#variants = this.#parseVariants();
    this.#productCard =
      this.closest('product-card') ||
      this.closest('.resource-card') ||
      this.closest('[data-resource-type="product"]');

  }

  /**
   * Add the first available / default variant (no size picker).
   * @param {Event} [event]
   */
  addSimple = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const eventTarget = event?.target;
    const button =
      eventTarget instanceof HTMLButtonElement
        ? eventTarget
        : eventTarget instanceof Element
          ? eventTarget.closest('button')
          : null;

    if (button instanceof HTMLButtonElement && button.disabled) return;

    const variant = this.#variants.find((item) => item.available) || this.#variants[0];
    if (!variant?.available) return;

    if (button instanceof HTMLButtonElement) button.disabled = true;

    try {
      await this.#addToCart(variant.id);
    } catch (error) {
      console.error(error);
    } finally {
      if (button instanceof HTMLButtonElement && variant.available) button.disabled = false;
    }
  };

  /**
   * @param {Event} event
   */
  addSize = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const eventTarget = event.target;
    const button =
      eventTarget instanceof HTMLButtonElement && eventTarget.dataset.size != null
        ? eventTarget
        : eventTarget instanceof Element
          ? eventTarget.closest('button[data-size]')
          : null;

    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const size = button.dataset.size?.trim();
    if (!size) return;

    const variant = this.#findVariantForSize(size);
    if (!variant?.available) return;

    button.disabled = true;

    try {
      await this.#addToCart(variant.id);
    } catch (error) {
      console.error(error);
    } finally {
      if (variant.available) button.disabled = false;
    }
  };

  #parseVariants() {
    try {
      const parsed = JSON.parse(this.refs.variantsData.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} size
   * @returns {VariantData | undefined}
   */
  #findVariantForSize(size) {
    const selectedOptions = this.#getSelectedOptions();
    selectedOptions[this.#sizeOptionIndex] = size;

    const exactMatch = this.#variants.find((variant) =>
      variant.options.every((option, index) => String(option) === String(selectedOptions[index]))
    );
    if (exactMatch?.available) return exactMatch;

    return this.#variants.find(
      (variant) => variant.available && String(variant.options[this.#sizeOptionIndex]) === String(size)
    );
  }

  /**
   * @returns {string[]}
   */
  #getSelectedOptions() {
    const productCard =
      this.#productCard ??
      this.closest('product-card') ??
      this.closest('.resource-card') ??
      this.closest('[data-resource-type="product"]');
    const optionCount = this.#variants[0]?.options.length || 1;
    /** @type {string[]} */
    const options = Array.from({ length: optionCount }, (_, index) =>
      String(this.#variants[0]?.options[index] || '')
    );

    if (productCard instanceof HTMLElement && 'getSelectedVariantId' in productCard) {
      const selectedId = /** @type {{ getSelectedVariantId: () => string | null }} */ (
        productCard
      ).getSelectedVariantId();
      if (selectedId) {
        const selectedVariant = this.#variants.find((variant) => String(variant.id) === String(selectedId));
        if (selectedVariant) return selectedVariant.options.map(String);
      }
    }

    const checkedSwatch = productCard?.querySelector('input[type="radio"]:checked[data-variant-id]');
    if (checkedSwatch instanceof HTMLInputElement && checkedSwatch.dataset.variantId) {
      const selectedVariant = this.#variants.find(
        (variant) => String(variant.id) === String(checkedSwatch.dataset.variantId)
      );
      if (selectedVariant) return selectedVariant.options.map(String);
    }

    const available = this.#variants.find((variant) => variant.available);
    return available ? available.options.map(String) : options;
  }

  /**
   * @param {number} variantId
   */
  async #addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];

    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    }

    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });

    const data = await response.json();

    if (data.status) {
      throw new Error(data.description || data.message || 'Cart error');
    }

    document.dispatchEvent(
      new CartAddEvent(data, this.id || this.dataset.productId || '', {
        source: 'card-quick-shop',
        itemCount: 1,
        productId: this.dataset.productId,
        variantId: String(variantId),
        sections: data.sections,
      })
    );
  }
}

if (!customElements.get('card-quick-shop')) {
  customElements.define('card-quick-shop', CardQuickShop);
}
